import { Fragment, useEffect, useRef, useState, useCallback } from "react";
import RowComponent from "./components/RowComponent";
import { TableHeader } from "./components/TableHeader";
import { NoDataMessage, StyledCell, StyledTable } from "./table.styles";
import { useTheme } from "@mui/material";
import {
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer, notUndefined } from "@tanstack/react-virtual";
import { buildFormattedColumns } from "./buildColumns.helper";
import { DefaultRow, TableProps } from "./types";
import { Theme } from "../Theme/types";
import { Loading } from "../Loading";
import { calculateRowSize } from "./table.helpers";

const BOTTOM_GAP = 8;
export const DEFAULT_COLUMN_SIZE = 111;
export const MIN_COLUMN_SIZE = 80;

const Table = <TRow extends DefaultRow>({
  data,
  columns,
  style: { rowHeight = 40, maxHeight, ...style },
  className,
  rowClassName = "",
  getRowClassName,
  rowDetailComponent,
  onChangeSelectedRows,
  onRowClick,
  selectedRows,
  overrideTableOptions,
  emptyDataMessage,
  isLoading,
  shouldExpandOnRowClick = true,
}: TableProps<TRow>) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const theme = useTheme<Theme>();
  const parentRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selectedRowsIds = Object.keys(rowSelection).filter(
      (id) => rowSelection[id]
    );
    if (onChangeSelectedRows) {
      onChangeSelectedRows(selectedRowsIds);
    }
  }, [rowSelection]);

  const getRowId = (originalRow: TRow) => {
    return String(originalRow.id);
  };

  const parsedSelectedRows = selectedRows?.reduce((acc, rowId) => {
    acc[rowId as string] = true;
    return acc;
  }, {} as Record<string, boolean>);

  useEffect(() => {
    if (parsedSelectedRows) {
      setRowSelection(parsedSelectedRows);
      // Expande o pai se uma row filha estiver selecionada
      const expanded: Record<string, boolean> = {};
      data.forEach((row: any) => {
        if (row.children && Array.isArray(row.children)) {
          const hasSelectedChild = row.children.some(
            (child: any) => parsedSelectedRows[child.id]
          );
          if (hasSelectedChild) {
            expanded[row.id] = true;
          }
        }
      });

      table.setExpanded(expanded);
    }

    const emptySelected = selectedRows?.length === 0;
    if (emptySelected) {
      table.toggleAllRowsExpanded(false);
    }
  }, [selectedRows]);

  const formattedColumns = buildFormattedColumns(
    columns,
    !!onChangeSelectedRows
  );

  const table = useReactTable<TRow>({
    data,
    columns: formattedColumns,
    getRowCanExpand: (row) =>
      Boolean(rowDetailComponent) || row.subRows.length > 0,
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: !!onChangeSelectedRows,
    getRowId,
    getSubRows: (originalRow) =>
      originalRow.children?.map((childRow) => ({ ...childRow })) as TRow[],
    defaultColumn: { size: DEFAULT_COLUMN_SIZE },
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),

    ...overrideTableOptions,
  });

  const { rows } = table.getRowModel();
  const getRowSize = useCallback(
    (index: number): number => calculateRowSize(index, rows, rowHeight),
    [rows, rowHeight]
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: getRowSize,
    overscan: 5,
  });

  const columnsSize = table.getAllColumns().map((column) => column.getSize());

  const items = virtualizer.getVirtualItems();
  const [before, after] =
    items.length > 0
      ? [
          notUndefined(items[0]).start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() -
            notUndefined(items[items.length - 1]).end,
        ]
      : [0, 0];

  const minHeight = 2 * rowHeight + BOTTOM_GAP;
  const defaultTableMaxHeight = wrapperRef.current?.clientHeight
    ? wrapperRef.current.clientHeight - (rowHeight + BOTTOM_GAP)
    : minHeight;

  const [computedMinHeight, setComputedMinHeight] = useState<number>(minHeight);

  const updateMinHeight = () => {
    if (wrapperRef.current)
      setComputedMinHeight(wrapperRef.current.clientHeight);
  };

  useEffect(() => {
    updateMinHeight();

    window.addEventListener("resize", updateMinHeight);
    return () => {
      window.removeEventListener("resize", updateMinHeight);
    };
  }, []);

  return (
    <div style={style} className={className} ref={wrapperRef}>
      <StyledTable
        theme={theme}
        props={{
          rowHeight,
          columnsSize 
        }}
        className="table"
        style={{
          minHeight: computedMinHeight || minHeight,
        }}
      >
        <TableHeader table={table} columnsSize={columnsSize} />
        {data.length === 0 && !isLoading && emptyDataMessage && (
          <NoDataMessage className="table-no-data-available">
            {emptyDataMessage}
          </NoDataMessage>
        )}
        {isLoading && <Loading center />}
        {!isLoading && data.length > 0 && (
          <div
            className={`table-body`}
            style={{
              height: maxHeight ?? defaultTableMaxHeight,
            }}
            ref={parentRef}
          >
            {before > 0 && <div style={{ height: before }} />}
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index] as Row<TRow>;
              const dynamicClassName =
                getRowClassName?.(row.original, row.getIsSelected()) ?? "";

              return (
                <Fragment key={`${row.id}-${virtualRow.index}`}>
                  <RowComponent
                    row={row}
                    rows={rows}
                    table={table}
                    columnsSize={columnsSize}
                    className={`${rowClassName} ${dynamicClassName}`}
                    style={{ height: `${rowHeight}px` }}
                    onRowClick={onRowClick}
                    onVirtualizerMeasure={() => virtualizer.measure()}
                    shouldExpandOnRowClick={shouldExpandOnRowClick}
                  />
                  {row.getIsExpanded() &&
                    row.subRows.map((subRow) => {
                      return (
                        <RowComponent
                          key={subRow.id}
                          row={subRow}
                          columnsSize={columnsSize}
                          style={{ height: `${rowHeight}px` }}
                          onRowClick={onRowClick}
                          table={table}
                          onVirtualizerMeasure={() => virtualizer.measure()}
                          shouldExpandOnRowClick={shouldExpandOnRowClick}
                        />
                      );
                    })}
                  {rowDetailComponent && row.getIsExpanded() && (
                    <StyledCell
                      style={{
                        gridColumn: `1 / ${row.getVisibleCells().length + 1}`,
                      }}
                      className={"table-cell"}
                    >
                      {rowDetailComponent(row.original)}
                    </StyledCell>
                  )}
                </Fragment>
              );
            })}
            {after > 0 && <div style={{ height: after }} />}
          </div>
        )}
      </StyledTable>
    </div>
  );
};
export default Table;
